import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

// 🌐 1. 定义语言包字典
const i18n = {
  zh: {
    title: "智能材料计算助手",
    subtitle: "(AI 驱动)",
    desc: "您可以询问热力学理论、电池原理，或直接输入自然语言筛选数据库中的材料物性。",
    placeholder: "例如：寻找带隙大于 2 eV 的立方晶系材料...",
    sendBtn: "发送",
    loadingBtn: "思考中...",
    exampleTitle: "试试这些查询：",
    aiHeader: "AI 回答：",
    crystal: "晶系",
    bandGap: "带隙",
    youngs: "杨氏模量",
    clickDetail: "点击查看完整属性及结构 →",
    examples: [
      "什么是热容？",
      "如何读声子谱图像？",
      "帮我找带隙大于 2 eV 的立方晶系材料",
      "查询杨氏模量大于1000GPa的稳定材料"
    ]
  },
  en: {
    title: "Smart Materials Assistant",
    subtitle: "(AI Powered)",
    desc: "Ask about thermodynamic theories, battery principles, or use natural language to filter material properties.",
    placeholder: "e.g., Find cubic materials with a band gap greater than 2 eV...",
    sendBtn: "Send",
    loadingBtn: "Thinking...",
    exampleTitle: "Try these queries:",
    aiHeader: "AI Response:",
    crystal: "Crystal System",
    bandGap: "Band Gap",
    youngs: "Young's Modulus",
    clickDetail: "Click for full properties & structure →",
    examples: [
      "What is heat capacity?",
      "How to read a phonon dispersion curve?",
      "Find cubic materials with a band gap greater than 2 eV",
      "Find stable materials with Young's modulus > 1000 GPa"
    ]
  }
};

const ThermoAssistant = () => {
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 🌐 2. 语言状态管理 (默认中文)
  const [lang, setLang] = useState("zh");
  const t = i18n[lang]; // 当前语言包的引用快捷方式

  const handleAsk = async (queryText) => {
    const textToAsk = typeof queryText === "string" ? queryText : question;
    if (!textToAsk.trim()) return;

    setIsLoading(true);
    setResponse(null);

    try {
      // 🌟 1. 全面换用 axios.post，走相对路径！
      // 它会自动拼接 baseUrl 并自动在 Header 里塞入 Token，再也不会报 401 了！
      const res = await axios.post("/api/chat/ask", {
        prompt: textToAsk
      });

      // 🌟 2. Axios 自动解析 JSON，直接拿数据
      setResponse(res.data);

    } catch (error) {
      // 🌟 3. Axios 的高级错误捕获逻辑
      let errorMsg = error.message;

      // 如果后端有返回具体的错误状态码（如 401, 403 等）
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail; // 捕获 FastAPI 返回的 detail 信息

        if (status === 401) {
          errorMsg = lang === "zh" ? "未登录或登录已过期，请重新登录。" : "Session expired. Please log in again.";
        } else if (status === 403 || status === 402) {
          // 动态读取后端传来的额度用尽提示，如果没有则用默认提示
          errorMsg = detail || (lang === "zh" ? "查询次数已用尽，请等待额度重置。" : "Query limit exceeded. Please upgrade.");
        } else {
          errorMsg = detail || `服务器开小差了 (Status: ${status})`;
        }
      }

      setResponse({
        type: "text",
        message: lang === "zh"
          ? `请求失败: ${errorMsg}`
          : `Request failed: ${errorMsg}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "40px auto",
        padding: "24px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        background: "#ffffff",
        position: "relative" // 用于定位右上角的语言切换按钮
      }}
    >
      {/* 🌐 3. 语言切换按钮 */}
      <button
        onClick={() => setLang(lang === "zh" ? "en" : "zh")}
        style={{
          position: "absolute",
          top: "24px",
          right: "24px",
          padding: "4px 12px",
          fontSize: "12px",
          background: "#f3f4f6",
          border: "1px solid #d1d5db",
          borderRadius: "16px",
          cursor: "pointer",
          color: "#374151",
          fontWeight: "bold"
        }}
      >
        {lang === "zh" ? "🌐 English" : "🌐 中文"}
      </button>

      <h3 style={{ marginBottom: "12px", color: "#1f2937", paddingRight: "80px" }}>
        {t.title} <span style={{ color: "#2563eb", fontSize: "14px", fontWeight: "normal" }}>{t.subtitle}</span>
      </h3>

      <p style={{ color: "#666", fontSize: "14px", paddingRight: "40px" }}>
        {t.desc}
      </p>

      {/* 输入区 */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAsk()}
          placeholder={t.placeholder}
          style={{
            flex: 1,
            padding: "10px 14px",
            fontSize: "14px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            outline: "none"
          }}
          disabled={isLoading}
        />
        <button
          onClick={handleAsk}
          disabled={isLoading}
          style={{
            padding: "0 20px",
            background: isLoading ? "#9ca3af" : "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: isLoading ? "not-allowed" : "pointer"
          }}
        >
          {isLoading ? t.loadingBtn : t.sendBtn}
        </button>
      </div>

      {/* 示例问题 */}
      <div style={{ marginTop: "16px" }}>
        <div style={{ fontSize: "13px", color: "#888", marginBottom: "8px" }}>
          {t.exampleTitle}
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {t.examples.map((q) => (
            <button
              key={q}
              onClick={() => {
                setQuestion(q);
                handleAsk(q);
              }}
              style={{
                fontSize: "12px",
                padding: "6px 12px",
                background: "#f3f4f6",
                color: "#4b5563",
                border: "1px solid #e5e7eb",
                borderRadius: "16px",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* 回答区 */}
      {response && (
        <div
          style={{
            marginTop: "24px",
            padding: "16px",
            background: "#f8fafc",
            borderLeft: "4px solid #3b82f6",
            borderRadius: "0 8px 8px 0"
          }}
        >
          <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#1e293b" }}>
            {t.aiHeader}
          </div>

          <div style={{ color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {response.message}
          </div>

          {response.type === "database_links" && response.data && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "12px",
              marginTop: "16px"
            }}>
              {response.data.map((mat) => (
                <Link
                  key={mat._id || mat.id}
                  to={`/materials/${mat._id || mat.id}`}
                  style={{
                    display: "block",
                    padding: "12px",
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    textDecoration: "none",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    transition: "box-shadow 0.2s"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)"}
                >
                  <div style={{ color: "#2563eb", fontWeight: "bold", fontSize: "16px", marginBottom: "4px" }}>
                    {mat.formula}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748b", display: "flex", flexDirection: "column", gap: "4px" }}>
                    <div>{t.crystal}: {mat.crystal_system || "N/A"}</div>
                    {mat.band_gap !== undefined && <div>{t.bandGap}: {mat.band_gap} eV</div>}
                    {mat.young_modulus !== undefined && <div>{t.youngs}: {mat.young_modulus} GPa</div>}
                  </div>
                  <div style={{ marginTop: "12px", fontSize: "11px", color: "#3b82f6", fontWeight: "500" }}>
                    {t.clickDetail}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ThermoAssistant;